import {
  EventStatusId,
  IncidentAttributeId,
  IncidentId,
} from '@collector/sportsapi-client-legacy'

import { UseAssignIncidentOnPitch } from '../components/RelationView/Main/PitchWithIncidents/IncidentsOverlay/useAssignIncidentOnPitch'
import { BlockedIncidentsMap } from './blockedIncidentsMap'
import { BlockingStrategy, ConfirmingStrategy } from './types'

export type IncidentButtonData<
  T extends IncidentId = IncidentId,
  T2 extends IncidentAttributeId = IncidentAttributeId,
> = {
  incidentId: T
  attributeId?: T2
  coordinates?: {
    x: number
    y: number
  }
  name?: string
  class?: string
  withIcon?: 'top' | 'left'
  /** `true` - prevent adding more than one incident with `Confirmation.ToBeDone` */
  canAddOneTbd?: boolean
  breakLine?: boolean
  blockingStrategy?: BlockingStrategy
  confirmingStrategy?: ConfirmingStrategy
  /** Custom click handler.
   * Receives `addIncident` — call it to send to API, or omit to skip.
   * `setIncident` — call it if you want to assign incident to the pitch without sending it to API
   */
  onClick?: (
    addIncident: () => void,
    setIncidentOpts?: {
      incidentId: IncidentButtonData['incidentId']
      participantTeamId: number
      setIncident: UseAssignIncidentOnPitch['setIncident']
    },
  ) => void
}

export type PitchIncidents<
  T1 extends IncidentId,
  T2 extends IncidentAttributeId,
> = {
  separator?: {
    rowStart: number
    colStart: number
    rowSpan: number
  }
  buttonHeightClass?: string
  gridClass: string
  incidentButtonData:
    IncidentButtonData<T1, T2>[] | IncidentButtonData<T1, T2>[][]
}

export type PitchIncidentsConfig<
  T1 extends EventStatusId = EventStatusId,
  T2 extends IncidentId = IncidentId,
  T3 extends IncidentAttributeId = IncidentAttributeId,
> = {
  pitchIncidents: PitchIncidents<T2, T3>
  blockedPitchIncidentsMap: BlockedIncidentsMap<T1, T2>
  eventStatusToBlockedPitchIncidentsTextMap?: Map<T1, string>
}

export function getPitchIncidentIds<T extends IncidentId>(
  incidentButtonData:
    | IncidentButtonData<T, IncidentAttributeId>[]
    | IncidentButtonData<T, IncidentAttributeId>[][],
): Set<T> {
  return new Set(incidentButtonData.flat().map((item) => item.incidentId))
}

export function getBlockedPitchIncidentsMap<
  T1 extends EventStatusId,
  T2 extends IncidentId,
>(
  eventStatuses: Set<T1>,
  incidentButtonData:
    | IncidentButtonData<T2, IncidentAttributeId>[]
    | IncidentButtonData<T2, IncidentAttributeId>[][],
): BlockedIncidentsMap<T1, T2> {
  const pitchIncidentIds = getPitchIncidentIds(incidentButtonData)

  return new Map([...eventStatuses].map((status) => [status, pitchIncidentIds]))
}
